<template>
    <div class="auth-page fill-height">
        <div class="auth-center">
            <div class="auth-card">

                <!-- Heading -->
                <div class="auth-heading mb-8">
                    <h1 class="auth-title">{{ loginTeacher ? 'Teachers Sign In' : 'Welcome back' }}</h1>
                    <p class="auth-subtitle mt-1">Sign in to your account to continue</p>
                    <v-btn
                        v-if="loginTeacher"
                        small text color="#D4780A"
                        :to="{name: 'login'}"
                        class="teacher-toggle-btn mt-2 pa-0"
                    >
                        Student sign in →
                    </v-btn>
                </div>

                <!-- Form -->
                <form @submit.prevent="login">
                    <div class="input-group mb-5">
                        <label class="input-label">Email address</label>
                        <v-text-field
                            v-model="email"
                            placeholder="you@example.com"
                            name="email"
                            :loading="loading"
                            :disabled="loading"
                            outlined
                            hide-details
                            class="auth-input"
                        />
                    </div>

                    <div class="input-group mb-5">
                        <div class="d-flex align-center justify-space-between mb-1">
                            <label class="input-label mb-0">Password</label>
                            <router-link v-if="!loginTeacher" to="/reset-password" class="forgot-link">
                                Forgot password?
                            </router-link>
                        </div>
                        <v-text-field
                            v-model="password"
                            placeholder="Enter your password"
                            outlined
                            name="password"
                            hide-details
                            :loading="loading"
                            :disabled="loading"
                            class="auth-input"
                            :append-icon="showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                            :type="showPassword ? 'text' : 'password'"
                            @click:append="showPassword = !showPassword"
                        />
                    </div>

                    <v-checkbox
                        v-model="remember"
                        :disabled="loading"
                        label="Keep me signed in"
                        hide-details
                        color="#D4780A"
                        class="remember-checkbox mb-6"
                    />

                    <v-btn
                        type="submit"
                        block
                        depressed
                        x-large
                        :disabled="loading"
                        :loading="loading"
                        color="#D4780A"
                        class="submit-btn white--text"
                        @click.prevent="login"
                    >
                        Sign In
                    </v-btn>
                </form>

                <template v-if="!loginTeacher">
                    <p class="setup-prompt text-center mt-5">
                        Signed up but never set a password?
                        <router-link :to="{name: 'reset-password', query: {setup: 1}}" class="setup-link">Set it now</router-link>
                    </p>

                    <div class="or-divider my-6">
                        <span>or</span>
                    </div>

                    <v-btn
                        block
                        large
                        depressed
                        outlined
                        :to="{name: 'login-teachers'}"
                        class="alt-btn mb-6"
                    >
                        <v-icon left size="18" color="#707070">mdi-school-outline</v-icon>
                        <span>Sign in as a Teacher</span>
                    </v-btn>

                    <p class="register-prompt text-center">
                        Don't have an account?
                        <router-link :to="{name: 'register'}" class="register-link">Create one free</router-link>
                    </p>
                </template>

            </div>
        </div>
        <slot name="footer" />
    </div>
</template>

<script>

// @ is an alias to /src
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import AppAPI from '@/app-api'

export default {
    name: 'login',
    data(){
        return {
            email: '',
            password: '',
            remember: true,
            loading: false,
            showPassword: false,
        };
    },
    components: {

    },
    computed:{
        loginTeacher(){
            return this.$route.name === 'login-teachers';
        },
        ...GlobalComputed
    },
    methods: {
        ...GlobalMethods,
        login(){
            const self = this;

            let email = this.email,
                password = this.password,
                remember = this.remember;

            if (this.capRunning && this.isProduction){
                AppAPI.Notifications.getDeviceToken(response=>{
                    const deviceToken = response.token;
                    window.console.log('EBM: Device token (' + deviceToken + ')');
                    self.loginAction(email, password, remember, deviceToken);
                }, error=>{
                    console.error('EBM: ' + JSON.stringify(error));
                    self.loginAction(email, password, remember);
                })
            }else{
                self.loginAction(email, password, remember);
            }
        },
        loginAction(email, password, remember = true, deviceToken=null){

            if (email && password){
                this.loading = true;
                let data = { email, password, remember, teacher: this.loginTeacher };
                if (deviceToken){
                    data.deviceToken = deviceToken;
                }

                this.$store.dispatch('loginAction', data).then(()=>{
                    this.loading = false;
                    this.$router.push({name: 'home'});
                }).catch(error=>{
                    this.loading = false;
                    if (error.response && (error.response.status === 401)){
                        if (error.response.data.message === 'SUSPENDED_ACCOUNT'){
                            this.showLoginMsg('Your account has been suspended, contact support.');
                        }else{
                            this.showLoginMsg('Email or Password is incorrect');
                        }
                    }else if (error.response && error.response.status === 422){
                        this.showLoginMsg('Data you entered is not valid');
                    }else{
                        this.showLoginMsg('Error occurred during signing you in');
                    }
                })
            }else{
                this.showLoginMsg("Inputs are required")
            }
        },
        showLoginMsg(msg){
            this.showMsg(msg, 'error', false);
        },
        closeMsg(){
            if (this.$store.state.globalModule.snackMsg.text){
                this.$store.dispatch('addSnackMsg', {show: false, text: '', msgBar: true});
            }
        },
    },
    watch: {
        email(){
            this.closeMsg();
        },
        password(){
            this.closeMsg();
        }
    },
    mounted(){


    }
}
</script>

<style lang="scss" scoped>
.auth-page {
    background: #F5F5F5;
}

.auth-center {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 16px;
}

.auth-card {
    width: 100%;
    max-width: 440px;
    background: #FFFFFF;
    border-radius: 16px;
    border: 1px solid #EBEBEB;
    padding: 40px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

    @media (max-width: 480px) {
        padding: 32px 24px;
        border-radius: 12px;
    }
}

/* ── Heading ── */
.auth-title {
    font-family: 'Inter', sans-serif;
    font-size: 26px;
    font-weight: 700;
    color: #1E1E1E;
    letter-spacing: -0.4px;
    line-height: 1.2;
}

.auth-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #8A8A8A;
    margin: 0;
}

.teacher-toggle-btn {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 600;
    min-width: 0 !important;
    height: auto !important;
}

/* ── Inputs ── */
.input-label {
    display: block;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
}

.auth-input {
    ::v-deep .v-input__slot {
        border-radius: 10px !important;
        background: #FAFAFA !important;
        min-height: 46px !important;
    }

    ::v-deep .v-text-field__slot input {
        font-family: 'Inter', sans-serif;
        font-size: 14.5px;
        color: #1E1E1E;
        padding: 10px 0 !important;
    }

    ::v-deep .v-text-field__slot input::placeholder {
        color: #C4C4C4;
    }

    ::v-deep .v-input--is-focused:not(.error--text) fieldset {
        border-color: #D4780A !important;
        border-width: 1.5px !important;
    }
}

.forgot-link {
    font-family: 'Inter', sans-serif;
    font-size: 12.5px;
    font-weight: 600;
    color: #D4780A;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

.setup-prompt {
    font-family: 'Inter', sans-serif;
    font-size: 13.5px;
    line-height: 1.5;
    color: #9E9E9E;
    margin: 0;
}

.setup-link {
    color: #D4780A;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
        text-decoration: underline;
    }
}

.remember-checkbox {
    margin-top: 0 !important;

    ::v-deep .v-label {
        font-family: 'Inter', sans-serif;
        font-size: 13.5px;
        color: #707070;
    }
}

/* ── CTA ── */
.submit-btn {
    height: 48px !important;
    border-radius: 10px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.2px;
    text-transform: none;
}

/* ── Divider ── */
.or-divider {
    position: relative;
    text-align: center;

    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        height: 1px;
        background: #F0F0F0;
    }

    span {
        position: relative;
        background: white;
        padding: 0 14px;
        font-family: 'Inter', sans-serif;
        font-size: 11px;
        color: #BDBDBD;
        text-transform: uppercase;
        letter-spacing: 1.5px;
    }
}

/* ── Teacher alt button ── */
.alt-btn {
    height: 46px !important;
    border-radius: 10px !important;
    border-color: #E8E8E8 !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #707070 !important;
}

/* ── Sign up prompt ── */
.register-prompt {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #9E9E9E;
    margin: 0;
}

.register-link {
    color: #D4780A;
    font-weight: 600;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}
</style>
